import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Building2, Plus, Inbox, FileText, MapPin, CalendarClock, Check, X, CalendarPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";
import { AttachmentList } from "../components/site/AttachmentList";
import { NotificationBell } from "../components/site/NotificationBell";
import { MessagesInbox } from "../components/site/MessagesInbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

const STATUS_STYLE = {
  Submitted: "bg-[#54BCE6] text-[#133955]", Assigned: "bg-[#FFE8B0] text-[#7a5b00]",
  "Walkthrough Requested": "bg-[#FFE8B0] text-[#7a5b00]", "Walkthrough Scheduled": "bg-[#C7F0D8] text-[#0f6b3d]",
  Quoted: "bg-[#133955] text-white", "Quote Accepted": "bg-[#C7F0D8] text-[#0f6b3d]", "Quote Declined": "bg-[#FBD5D5] text-[#9b1c1c]",
};
const Badge = ({ s, testid }) => <span data-testid={testid} className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_STYLE[s] || "bg-[#EAF7FC] text-[#133955]"}`}>{s}</span>;

const EMPTY = { property_address: "", mls_number: "", target_close_date: "", repairs: "" };

export default function AgentPortal() {
  const { token, user, role, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openId, setOpenId] = useState(null);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!authLoading && !token) navigate("/agent/login");
    if (!authLoading && token && role && role !== "agent") navigate(role === "admin" ? "/admin" : "/portal");
  }, [authLoading, token, role, navigate]);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/agent/repair-requests`, { headers: { Authorization: `Bearer ${token}` } });
    setRequests(data.requests || []);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try { await load(); }
      catch (err) { if (err?.response?.status === 401) { logout(); navigate("/agent/login"); } }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [token, load, logout, navigate]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link: /agent?repair=<id> opens that request's detail from a notification
  useEffect(() => {
    if (loading) return;
    const repair = searchParams.get("repair");
    if (!repair) return;
    if (requests.some((r) => r.id === repair)) setOpenId(repair);
    else { toast.error("That repair request could not be found."); setSearchParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading]);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!form.property_address || !form.repairs) { toast.error("Property address and repairs are required."); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/agent/repair-requests`, form, authHeader);
      const rid = data.request.id;
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f); fd.append("label", f.name);
        await axios.post(`${API}/agent/repair-requests/${rid}/documents`, fd, { headers: { ...authHeader.headers, "Content-Type": "multipart/form-data" } });
      }
      toast.success("Repair request submitted.");
      setNewOpen(false); setForm(EMPTY); setFiles([]); load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not submit request.");
    } finally { setSaving(false); }
  };

  if (authLoading || loading) return <div className="min-h-screen grid place-items-center bg-[#EAF7FC]"><Loader2 className="animate-spin text-[#133955]" size={32} /></div>;

  return (
    <div className="min-h-screen bg-[#EAF7FC]" data-testid="agent-portal">
      <header className="bg-white border-b border-[rgba(19,57,85,0.08)] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <NotificationBell />
            <MessagesInbox />
            <button data-testid="agent-logout" onClick={() => { logout(); navigate("/agent/login"); }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-5 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors"><LogOut size={16} /> Sign Out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#133955] text-white"><Building2 size={22} /></span>
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#133955] tracking-tight">Repair Quote Requests</h1>
              <p className="text-[#173042]/70 mt-0.5">Welcome, {user?.name}</p>
            </div>
          </div>
          <button data-testid="new-request-btn" onClick={() => setNewOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-6 py-3 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors"><Plus size={18} /> New Repair Request</button>
        </div>

        {requests.length === 0 ? (
          <div className="text-center py-20 rounded-2xl bg-white border border-[rgba(19,57,85,0.08)]" data-testid="no-requests">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#EAF7FC] text-[#54BCE6] mx-auto mb-4"><Inbox size={26} /></span>
            <p className="text-[#173042] font-semibold">No repair requests yet</p>
            <p className="text-[#173042]/60 text-sm mt-1">Submit a repair-quote request and upload your addendum to get started.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="requests-list">
            {requests.map((r) => (
              <button key={r.id} onClick={() => setOpenId(r.id)} data-testid={`request-row-${r.id}`} className="w-full text-left rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-5 hover:border-[#54BCE6] transition-colors">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-[#54BCE6]">{r.request_ref}</span>
                    <span className="font-bold text-[#133955]">{r.property_address}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(r.assigned_contractors?.length ? r.assigned_contractors : (r.assigned_contractor_ref ? [{ contractor_id: r.assigned_contractor_id, contractor_ref: r.assigned_contractor_ref }] : [])).map((e) => (
                      <span key={e.contractor_id} className="text-xs font-semibold text-[#0f6b3d]" data-testid={`request-contractor-${r.id}-${e.contractor_id}`}>{e.contractor_ref}{e.role_label ? ` · ${e.role_label}` : ""}</span>
                    ))}
                    <Badge s={r.status} testid={`request-status-${r.id}`} />
                  </div>
                </div>
                <p className="text-sm text-[#173042]/70 mt-1">{(r.documents || []).length} document(s){r.target_close_date ? ` · close ${r.target_close_date}` : ""}</p>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* New request modal */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-[#133955]">New Repair Quote Request</DialogTitle>
            <DialogDescription>Provide property details and upload your repair addendum & inspection report.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitNew} data-testid="new-request-form" className="space-y-3">
            <div><label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Property Address *</label><input data-testid="rr-address" className="hpc-input" value={form.property_address} onChange={(e) => setForm({ ...form, property_address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">MLS # (optional)</label><input data-testid="rr-mls" className="hpc-input" value={form.mls_number} onChange={(e) => setForm({ ...form, mls_number: e.target.value })} /></div>
              <div><label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Target Close Date</label><input data-testid="rr-close" type="date" className="hpc-input" value={form.target_close_date} onChange={(e) => setForm({ ...form, target_close_date: e.target.value })} /></div>
            </div>
            <div><label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Repairs / Notes *</label><textarea data-testid="rr-repairs" className="hpc-input min-h-[100px]" value={form.repairs} onChange={(e) => setForm({ ...form, repairs: e.target.value })} placeholder="List the repairs needed…" /></div>
            <div><label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Documents & Photos (addendum, inspection, repair photos)</label><input data-testid="rr-files" type="file" multiple accept="image/*,.pdf,.doc,.docx" onChange={(e) => setFiles(Array.from(e.target.files || []))} className="text-sm block w-full" />{files.length > 0 && <p className="text-xs text-[#173042]/60 mt-1">{files.length} file(s) selected</p>}</div>
            <DialogFooter>
              <button type="button" onClick={() => setNewOpen(false)} className="rounded-full px-6 py-3 font-bold text-[#133955] hover:bg-[#EAF7FC] transition-colors">Cancel</button>
              <button type="submit" data-testid="rr-submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-6 py-3 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-60">{saving && <Loader2 size={16} className="animate-spin" />} Submit Request</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <RequestDetail rid={openId} onClose={() => { setOpenId(null); if (searchParams.get("repair")) setSearchParams({}, { replace: true }); }} authHeader={authHeader} onChange={load} />
    </div>
  );
}

function RequestDetail({ rid, onClose, authHeader, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wtForm, setWtForm] = useState({ proposed_datetime: "", notes: "" });
  const [showCounter, setShowCounter] = useState(false);
  const fileToken = (authHeader.headers.Authorization || "").replace("Bearer ", "");

  const load = useCallback(async () => {
    if (!rid) return;
    setLoading(true);
    try { const { data } = await axios.get(`${API}/agent/repair-requests/${rid}`, authHeader); setData(data); }
    catch { toast.error("Could not load request."); onClose(); }
    finally { setLoading(false); }
  }, [rid]); // eslint-disable-line

  useEffect(() => { if (rid) load(); }, [rid, load]);
  if (!rid) return null;
  const rr = data?.request;
  const wt = rr?.walkthrough || { status: "none", proposals: [] };
  const latestContractorProp = [...(wt.proposals || [])].reverse().find((p) => p.by === "contractor");
  const refresh = async () => { await load(); onChange && onChange(); };

  const respondQuote = async (quoteId, decision) => {
    setBusy(true);
    try { await axios.post(`${API}/agent/repair-requests/${rid}/quotes/${quoteId}/respond`, { decision }, authHeader); toast.success(`Quote ${decision}ed.`); await refresh(); }
    catch { toast.error("Could not respond to quote."); } finally { setBusy(false); }
  };
  const confirmWt = async () => {
    setBusy(true);
    try { await axios.post(`${API}/agent/repair-requests/${rid}/walkthrough/confirm`, {}, authHeader); toast.success("Walk-through confirmed."); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm."); } finally { setBusy(false); }
  };
  const counterWt = async () => {
    if (!wtForm.proposed_datetime) { toast.error("Pick a date/time."); return; }
    setBusy(true);
    try { await axios.post(`${API}/agent/repair-requests/${rid}/walkthrough`, wtForm, authHeader); toast.success("New time proposed."); setWtForm({ proposed_datetime: "", notes: "" }); setShowCounter(false); await refresh(); }
    catch { toast.error("Could not propose time."); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!rid} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="agent-request-detail">
        {loading || !rr ? (
          <><DialogHeader><DialogTitle className="sr-only">Request</DialogTitle><DialogDescription className="sr-only">Loading</DialogDescription></DialogHeader><div className="grid place-items-center py-16"><Loader2 className="animate-spin text-[#133955]" size={28} /></div></>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-[#133955] flex items-center gap-2"><span className="font-mono text-[#54BCE6]">{rr.request_ref}</span> {rr.property_address}</DialogTitle>
              <DialogDescription>Status: <Badge s={rr.status} /> {(() => { const al = rr.assigned_contractors?.length ? rr.assigned_contractors : (rr.assigned_contractor_name ? [{ contractor_name: rr.assigned_contractor_name }] : []); return al.length ? `· Contractor${al.length > 1 ? "s" : ""}: ${al.map((e) => e.contractor_name + (e.role_label ? ` (${e.role_label})` : "")).join(", ")}` : "· Awaiting assignment"; })()}</DialogDescription>
            </DialogHeader>

            <div className="rounded-xl bg-[#EAF7FC] p-4 text-sm">
              {rr.mls_number && <p><span className="text-[#173042]/50">MLS:</span> {rr.mls_number}</p>}
              {rr.target_close_date && <p><span className="text-[#173042]/50">Target close:</span> {rr.target_close_date}</p>}
              <p className="mt-2 whitespace-pre-wrap text-[#173042]/80">{rr.repairs}</p>
            </div>

            {(rr.documents || []).length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Documents & Photos</p>
                <AttachmentList docs={rr.documents} token={fileToken} testid="agent-attachments" />
              </div>
            )}

            {/* Quotes */}
            <div className="mt-4" data-testid="agent-quotes">
              <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Quotes</p>
              {(data.quotes || []).length === 0 ? <p className="text-sm text-[#173042]/50">No quotes yet.</p> : (data.quotes || []).map((q) => (
                <div key={q.id} className="rounded-xl border border-[rgba(19,57,85,0.1)] p-3 mb-2" data-testid={`agent-quote-${q.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-[#133955]">{money(q.amount)}</span>
                    <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${q.status === "accepted" ? "bg-[#C7F0D8] text-[#0f6b3d]" : q.status === "declined" ? "bg-[#FBD5D5] text-[#9b1c1c]" : "bg-[#FFE8B0] text-[#7a5b00]"}`}>{q.status}</span>
                  </div>
                  {q.scope && <p className="text-sm text-[#173042]/70 mt-1">{q.scope}</p>}
                  {q.document && <a href={`${API}/files/${q.document.file_id}?auth=${fileToken}`} target="_blank" rel="noreferrer" className="text-xs text-[#54BCE6] hover:underline">view document</a>}
                  {q.status === "pending" && (
                    <div className="flex gap-2 mt-2">
                      <button data-testid={`accept-quote-${q.id}`} onClick={() => respondQuote(q.id, "accept")} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-[#0f6b3d] text-white px-4 py-2 text-sm font-bold hover:opacity-90 disabled:opacity-60"><Check size={14} /> Accept</button>
                      <button data-testid={`decline-quote-${q.id}`} onClick={() => respondQuote(q.id, "decline")} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-[#9b1c1c] text-white px-4 py-2 text-sm font-bold hover:opacity-90 disabled:opacity-60"><X size={14} /> Decline</button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Walkthrough */}
            <div className="mt-4" data-testid="agent-walkthrough">
              <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2 flex items-center gap-1.5"><CalendarClock size={13} /> Walk-through</p>
              {wt.status === "none" ? <p className="text-sm text-[#173042]/50">No walk-through requested yet.</p> : (
                <>
                  {wt.status === "confirmed" && <p className="text-sm font-semibold text-[#0f6b3d] mb-2" data-testid="wt-confirmed">Confirmed for {wt.confirmed_datetime}</p>}
                  <div className="space-y-1.5 mb-2">
                    {(wt.proposals || []).map((p) => <div key={p.id} className="text-sm text-[#173042]/80"><strong className="text-[#133955] capitalize">{p.by}:</strong> {p.proposed_datetime}{p.notes ? ` — ${p.notes}` : ""}</div>)}
                  </div>
                  {wt.status !== "confirmed" && latestContractorProp && (
                    <div className="flex flex-wrap gap-2">
                      <button data-testid="confirm-wt-btn" onClick={confirmWt} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-[#0f6b3d] text-white px-4 py-2 text-sm font-bold hover:opacity-90 disabled:opacity-60"><Check size={14} /> Confirm {latestContractorProp.proposed_datetime}</button>
                      <button data-testid="counter-wt-btn" onClick={() => setShowCounter((s) => !s)} className="inline-flex items-center gap-1 rounded-full border-2 border-[#133955] text-[#133955] px-4 py-2 text-sm font-bold hover:bg-[#133955] hover:text-white"><CalendarPlus size={14} /> Propose another time</button>
                    </div>
                  )}
                  {showCounter && wt.status !== "confirmed" && (
                    <div className="mt-3 rounded-xl border border-[rgba(19,57,85,0.1)] p-3">
                      <input data-testid="wt-datetime" type="datetime-local" value={wtForm.proposed_datetime} onChange={(e) => setWtForm({ ...wtForm, proposed_datetime: e.target.value })} className="hpc-input bg-white mb-2" />
                      <input data-testid="wt-notes" value={wtForm.notes} onChange={(e) => setWtForm({ ...wtForm, notes: e.target.value })} placeholder="Notes (optional)" className="hpc-input bg-white mb-2" />
                      <button data-testid="wt-propose-submit" onClick={counterWt} disabled={busy} className="w-full rounded-full bg-[#133955] text-white px-4 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] disabled:opacity-60">Send Proposed Time</button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Activity</p>
              <div className="space-y-2" data-testid="agent-activity">
                {(data.activities || []).map((a) => (
                  <div key={a.id} className="flex items-start gap-3 text-sm"><span className="mt-1 h-2 w-2 rounded-full bg-[#54BCE6] shrink-0" /><div><p className="text-[#173042]">{a.description}</p><p className="text-xs text-[#173042]/50">{fmt(a.created_at)} · {a.actor_role}</p></div></div>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
